/**
 * Modern utility for text formatting that replaces the deprecated document.execCommand
 */

/**
 * Applies formatting to selected text using modern APIs
 * @param {string} command - Formatting command ('bold', 'italic', 'underline')
 * @param {HTMLElement} element - contentEditable element where to apply formatting
 * @returns {boolean} - Operation success
 */
type FormattingCommand = 'bold' | 'italic' | 'underline' | 'link'

interface FormattingOptions {
  url?: string
}

export const applyTextFormatting = (
  command: FormattingCommand,
  element: HTMLElement,
  options: FormattingOptions = {}
) => {
  if (!element?.isContentEditable) {
    return false
  }

  element.focus()

  const selection = globalThis.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return false
  }

  try {
    const range = selection.getRangeAt(0)

    if (isFormattingActive(command)) {
      return removeFormatting(command, range)
    }

    return applyFormatting(command, range, selection, options)
  } catch {
    return false
  }
}

/**
 * Applies formatting to the selected range
 * @param {string} command - Formatting command
 * @param {Range} range - Selection range
 * @param {Selection} selection - Selection object
 * @returns {boolean} - Operation success
 */
const applyFormatting = (
  command: FormattingCommand,
  range: Range,
  selection: Selection,
  options: FormattingOptions = {}
) => {
  const selectedContent = range.extractContents()

  if (!selectedContent.textContent.trim()) {
    range.insertNode(selectedContent)
    return false
  }

  const formattingElement = createFormattingElement(command, options)
  if (!formattingElement) {
    range.insertNode(selectedContent)
    return false
  }

  formattingElement.appendChild(selectedContent)
  range.insertNode(formattingElement)

  range.setStartAfter(formattingElement)
  range.setEndAfter(formattingElement)
  selection.removeAllRanges()
  selection.addRange(range)

  return true
}

/**
 * Removes formatting from the selected range
 * @param {string} command - Formatting command to remove
 * @param {Range} range - Selection range
 * @returns {boolean} - Operation success
 */
const removeFormatting = (command: FormattingCommand, range: Range) => {
  const parentElement = getParentElement(range)

  let formattedElement: HTMLElement | null = parentElement
  while (formattedElement?.nodeType === Node.ELEMENT_NODE) {
    if (hasFormattingCommand(formattedElement, command)) {
      break
    }
    formattedElement = formattedElement.parentElement
  }

  if (!formattedElement || !hasFormattingCommand(formattedElement, command)) {
    return false
  }

  const parent = formattedElement.parentNode
  if (!parent) return false
  while (formattedElement.firstChild) {
    parent.insertBefore(formattedElement.firstChild, formattedElement)
  }
  formattedElement.remove()

  return true
}

/**
 * Creates formatting element based on command
 * @param {string} command - Formatting command
 * @returns {HTMLElement|null} - Created element or null
 */
const createFormattingElement = (command: FormattingCommand, options: FormattingOptions = {}): HTMLElement | null => {
  switch (command) {
    case 'bold':
      return document.createElement('strong')
    case 'italic':
      return document.createElement('em')
    case 'underline':
      return document.createElement('u')
    case 'link': {
      const url = (options?.url || '').trim()
      if (!url) return null
      const a = document.createElement('a')

      const normalizedUrl = /^(https?:)?\/\//.test(url) ? url : `https://${url}`
      a.setAttribute('href', normalizedUrl)
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
      return a
    }
    default:
      return null
  }
}

/**
 * Checks if an element has bold formatting
 * @param {HTMLElement} element - Element to check
 * @param {string} tagName - Tag name in lowercase
 * @returns {boolean} - If the element has bold formatting
 */
const isBoldElement = (element: HTMLElement, tagName: string): boolean => {
  const computedStyle = globalThis.getComputedStyle(element)
  const fontWeight = computedStyle.fontWeight

  return tagName === 'strong' || tagName === 'b' || fontWeight === 'bold' || Number.parseInt(fontWeight, 10) >= 700
}

/**
 * Checks if an element has italic formatting
 * @param {HTMLElement} element - Element to check
 * @param {string} tagName - Tag name in lowercase
 * @returns {boolean} - If the element has italic formatting
 */
const isItalicElement = (element: HTMLElement, tagName: string): boolean => {
  return tagName === 'em' || tagName === 'i' || globalThis.getComputedStyle(element).fontStyle === 'italic'
}

/**
 * Checks if an element has underline formatting
 * @param {HTMLElement} element - Element to check
 * @param {string} tagName - Tag name in lowercase
 * @returns {boolean} - If the element has underline formatting
 */
const isUnderlineElement = (element: HTMLElement, tagName: string): boolean => {
  return tagName === 'u' || globalThis.getComputedStyle(element).textDecoration.includes('underline')
}

/**
 * Checks if an element has the specified formatting
 * @param {HTMLElement} element - Element to check
 * @param {string} command - Formatting command
 * @returns {boolean} - If the element has the formatting
 */
const hasFormattingCommand = (element: HTMLElement, command: FormattingCommand): boolean => {
  const tagName = element.tagName.toLowerCase()

  switch (command) {
    case 'bold':
      return isBoldElement(element, tagName)
    case 'italic':
      return isItalicElement(element, tagName)
    case 'underline':
      return isUnderlineElement(element, tagName)
    case 'link':
      return tagName === 'a'
    default:
      return false
  }
}

/**
 * Gets the parent element of the selection range
 * @param {Range} range - Selection range
 * @returns {HTMLElement} - Parent element
 */
const getParentElement = (range: Range): HTMLElement => {
  if (range.commonAncestorContainer.nodeType === Node.TEXT_NODE) {
    return (range.commonAncestorContainer.parentElement ?? range.commonAncestorContainer) as HTMLElement
  }
  return range.commonAncestorContainer as HTMLElement
}

/**
 * Checks if a formatting command is active at the current cursor position
 * @param {string} command - Command to check ('bold', 'italic', 'underline')
 * @returns {boolean} - If the command is active
 */
export const isFormattingActive = (command: FormattingCommand): boolean => {
  try {
    const selection = globalThis.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    const parentElement = getParentElement(range)

    let currentElement: HTMLElement | null = parentElement
    while (currentElement?.nodeType === Node.ELEMENT_NODE) {
      if (hasFormattingCommand(currentElement, command)) {
        return true
      }
      currentElement = currentElement.parentElement
    }

    return false
  } catch {
    return false
  }
}
